import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import get from 'lodash/get';
import {
  Grid, InputLabel, Checkbox, FormControlLabel,
} from '@mui/material';
import { GoogleMap } from '@react-google-maps/api';
import PropTypes from 'prop-types';

import LocationMarkers from '../map/location-markers';
import ZonePolygons from '../map/zone-polygons';
import VehicleMarkers from '../map/vehicle-markers';
import MultiSelect from '../../common/multi-select';
import {
  LinkAdminVehicleZoneCategories,
  VehicleZoneCategories,
  VehicleZonesStatusNames,
} from '../../../lib/utils';

const styles = {
  mapDiv: {
    height: '450px',
    width: '840px',
  },
  mapFilters: {
    display: 'flex',
  },
  container: {
    paddingTop: '16px',
  },
  select: {
    width: '500px',
  },
  checkboxes: {
    marginLeft: '140px',
    marginTop: '16px',
  },
};

function BasicMap({
  zoom, center, zones, showLabels, locations, vehicles,
  onSelectLocation, activeLocations, currentRoute,
}) {
  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      zoom={zoom}
      center={center}
    >
      <VehicleMarkers
        isClustered={false}
        vehicles={vehicles}
        onClick={() => {}}
      />
      <ZonePolygons
        showLabels={showLabels}
        zones={zones}
        editable={false}
      />
      <LocationMarkers
        zIndex={vehicles.length + 2}
        locations={locations}
        onClick={onSelectLocation}
        activeLocations={activeLocations}
        currentRoute={currentRoute}
      />
    </GoogleMap>
  );
}

class RouteBuilderMap extends React.Component {
  static DEFAULT_ZOOM_LEVEL = 14;

  static DEFAULT_LAT = 0;

  static DEFAULT_LNG = 0;

  static DEFAULT_CENTER = { lat: RouteBuilderMap.DEFAULT_LAT, lng: RouteBuilderMap.DEFAULT_LNG };

  constructor(props) {
    super(props);
    const { centerOverride, selectedMarket } = props;

    const selectedMarketGeo = get(selectedMarket, 'geo.coordinates', [RouteBuilderMap.DEFAULT_LNG, RouteBuilderMap.DEFAULT_LAT]);
    const marketCenter = { lat: selectedMarketGeo[1], lng: selectedMarketGeo[0] };

    const center = centerOverride || marketCenter || RouteBuilderMap.DEFAULT_CENTER;
    const zoom = RouteBuilderMap.DEFAULT_ZOOM_LEVEL;

    this.state = {
      zoom,
      center,
      showLabels: false,
      filterCategory: [],
      scooterFilter: true,
    };

    this.onSelectLocation = this.onSelectLocation.bind(this);
    this.handleToggleLabels = this.handleToggleLabels.bind(this);
    this.handleCategoryFilter = this.handleCategoryFilter.bind(this);
    this.updateScooterFilter = this.updateScooterFilter.bind(this);
    this.updateLabelFilter = this.updateLabelFilter.bind(this);
  }

  onSelectLocation(location) {
    const {
      currentRouteLocations, setCurrentRouteLocations, changedLocations, setChangedLocations,
    } = this.props;
    const index = currentRouteLocations.indexOf(location);
    const potentialIndex = changedLocations.indexOf(location._id);

    if (index === -1) {
      currentRouteLocations.push(location);
      setCurrentRouteLocations([...currentRouteLocations]);
      changedLocations.push(location._id);
      setChangedLocations([...changedLocations]);
    } else if (currentRouteLocations.length === 1 && index === 0) {
      setCurrentRouteLocations([]);
    } else {
      currentRouteLocations.splice(index, 1);
      setCurrentRouteLocations([...currentRouteLocations]);
    }

    if (potentialIndex !== -1) {
      changedLocations.splice(potentialIndex, 1);
      setChangedLocations([...changedLocations]);
    }
  }

  handleToggleLabels() {
    this.setState(({ showLabels }) => ({ showLabels: !showLabels }));
  }

  handleCategoryFilter(value) {
    this.setState({ filterCategory: value });
  }

  updateScooterFilter(value) {
    this.setState({ scooterFilter: value });
  }

  updateLabelFilter(value) {
    this.setState({ showLabels: value });
  }

  render() {
    const {
      vehicles, vehicleZones, pickupZones, locations,
      currentRouteLocations, currentRoute,
    } = this.props;
    const {
      zoom,
      center,
      showLabels,
      filterCategory,
      scooterFilter,
    } = this.state;

    const activePickupZones = pickupZones.filter(
      (zone) => zone.status === VehicleZonesStatusNames.ACTIVE,
    );
    const activeVehicleZones = vehicleZones.filter(
      (zone) => zone.status === VehicleZonesStatusNames.ACTIVE,
    );
    const filteredVehicleZones = activeVehicleZones.filter(
      (zone) => filterCategory && filterCategory.includes(zone.category),
    );
    const zonesToDisplay = activePickupZones
      .filter(() => filterCategory && filterCategory.includes(VehicleZoneCategories.PICKUP))
      .concat(filteredVehicleZones);
    const vehiclesToDisplay = scooterFilter ? vehicles : [];

    return (
      <div style={styles.container}>
        <Grid style={styles.mapFilters}>
          <Grid style={styles.select} sx={{ mb: 1 }}>
            <InputLabel>
              Map Layers
            </InputLabel>
            <MultiSelect
              value={filterCategory}
              options={Object.keys(LinkAdminVehicleZoneCategories).map((c) => ({
                value: VehicleZoneCategories[c],
                label: LinkAdminVehicleZoneCategories[c],
                isDisabled: false,
              }))}
              handleChange={(value) => this.handleCategoryFilter(value)}
            />
          </Grid>
          <div style={styles.checkboxes}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={scooterFilter}
                  onClick={() => this.updateScooterFilter(!scooterFilter)}
                />
              )}
              label="Scooters"
              labelPlacement="start"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={showLabels}
                  onClick={() => this.updateLabelFilter(!showLabels)}
                />
              )}
              label="Labels"
              labelPlacement="start"
            />
          </div>
        </Grid>
        <div id="map" style={styles.mapDiv}>
          <BasicMap
            zoom={zoom}
            center={center}
            zones={zonesToDisplay}
            vehicles={vehiclesToDisplay}
            locations={locations}
            showLabels={showLabels}
            onSelectLocation={this.onSelectLocation}
            containerElement={<div style={{ height: '100%' }} />}
            mapElement={<div style={{ height: '100%' }} />}
            activeLocations={currentRouteLocations}
            currentRoute={currentRoute}
          />
        </div>
      </div>
    );
  }
}
RouteBuilderMap.propTypes = {
  vehicles: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  pickupZones: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  vehicleZones: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  currentRouteLocations: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  setCurrentRouteLocations: PropTypes.func.isRequired,
  changedLocations: PropTypes.arrayOf(
    PropTypes.string.isRequired,
  ).isRequired,
  setChangedLocations: PropTypes.func.isRequired,
};

function mapStateToProps({ markets }, { match }) {
  const marketId = get(match, 'params.marketId');

  return {
    selectedMarket: markets.selectedMarket,
    selectedMarketId: marketId,
  };
}

export default withRouter(connect(mapStateToProps)(RouteBuilderMap));
